/* import __COLOCATED_TEMPLATE__ from './data-fields.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  type UserField,
  type TicketField,
  type OrganizationField,
} from 'embercom/objects/standalone/data-fields/common';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import { post } from 'embercom/lib/ajax';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type Admin from 'embercom/models/admin';

interface Args {
  hideBorder?: boolean;
}

type App = { id: string; currentAdmin: Admin; reload: () => void };
type NotificationsService = {
  notifyResponseError: (
    e: Error,
    messages: { default: string },
    options: { responseProvidedErrors: number[] },
  ) => void;
};

export default class StandaloneZendeskDataFields extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: NotificationsService;
  @service declare store: Store;

  @tracked selectedField?: UserField | TicketField | OrganizationField;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.zendeskPlatform.zendeskDataLoader.loadZendeskDataFields).perform();
  }

  get zendeskPlatform() {
    return this.finStandaloneService.zendeskPlatform;
  }

  get app(): App {
    return this.finStandaloneService.app;
  }

  get zendeskConfig() {
    return this.finStandaloneService.zendeskConfig;
  }

  get synchronizedUserFields(): Array<UserField> {
    return this.zendeskPlatform.zendeskDataFields.userFields.filter(
      (field) => field.required || field.intercom_cda?.identifier || field.isSynchronizing,
    );
  }

  get unsynchronizedUserFields(): Array<UserField> {
    return this.zendeskPlatform.zendeskDataFields.userFields.filter(
      (field) => !field.required && !field.intercom_cda?.identifier && !field.isSynchronizing,
    );
  }

  get synchronizedTicketFields(): Array<TicketField> {
    return this.zendeskPlatform.zendeskDataFields.ticketFields.filter(
      (field) => field.required || field.intercom_cvda?.id || field.isSynchronizing,
    );
  }

  get unsynchronizedTicketFields(): Array<TicketField> {
    return this.zendeskPlatform.zendeskDataFields.ticketFields.filter(
      (field) => !field.required && !field.intercom_cvda?.id && !field.isSynchronizing,
    );
  }

  get synchronizedOrganizationFields(): Array<OrganizationField> {
    return this.zendeskPlatform.zendeskDataFields.organizationFields.filter(
      (field) => field.required || field.intercom_cda?.identifier || field.isSynchronizing,
    );
  }

  get unsynchronizedOrganizationFields(): Array<OrganizationField> {
    return this.zendeskPlatform.zendeskDataFields.organizationFields.filter(
      (field) => !field.required && !field.intercom_cda?.identifier && !field.isSynchronizing,
    );
  }

  get zendeskDataFields() {
    return this.zendeskPlatform.zendeskDataFields;
  }

  @action async addZendeskUserField(userField: UserField) {
    try {
      // Place field at the end of the list
      this.zendeskDataFields.userFields.removeObject(userField);
      this.zendeskDataFields.userFields.pushObject(userField);

      userField.isSynchronizing = true;
      let intercomCda: UserField['intercom_cda'] = await post(
        `/ember/standalone/zendesk_data/${userField.id}/synchronize_user_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      userField.intercom_cda = intercomCda;
      this.app.reload();
    } catch (e) {
      this.handleError(e);
    } finally {
      userField.isSynchronizing = false;
    }
  }

  @action async addZendeskTicketField(ticketField: TicketField) {
    try {
      // Place the field at the end of the list
      this.zendeskDataFields.ticketFields.removeObject(ticketField);
      this.zendeskDataFields.ticketFields.pushObject(ticketField);

      ticketField.isSynchronizing = true;
      let intercomCvda: TicketField['intercom_cvda'] = await post(
        `/ember/standalone/zendesk_data/${ticketField.id}/synchronize_ticket_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      ticketField.intercom_cvda = intercomCvda;
      this.app.reload();
    } catch (e) {
      this.handleError(e);
    } finally {
      ticketField.isSynchronizing = false;
    }
  }

  @action async addZendeskOrganizationField(organizationField: OrganizationField) {
    try {
      // Place field at the end of the list
      this.zendeskDataFields.organizationFields.removeObject(organizationField);
      this.zendeskDataFields.organizationFields.pushObject(organizationField);

      organizationField.isSynchronizing = true;
      let intercomCda: OrganizationField['intercom_cda'] = await post(
        `/ember/standalone/zendesk_data/${organizationField.id}/synchronize_organization_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      organizationField.intercom_cda = intercomCda;
      this.app.reload();
    } catch (e) {
      this.handleError(e);
    } finally {
      organizationField.isSynchronizing = false;
    }
  }

  @action didCloseModal() {
    this.selectedField = undefined;
    this.finStandaloneService.notifyPropertyChange('zendeskDataFields');
  }

  @action selectField(field: UserField | TicketField | OrganizationField) {
    this.selectedField = field;
  }

  handleError(e: Error & { jqXHR?: $TSFixMe }) {
    this.notificationsService.notifyResponseError(
      e,
      {
        default: 'Something went wrong and we couldn’t synchronize that field.',
      },
      {
        responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
      },
    );
    console.error(e.message || e.jqXHR);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Settings::DataFields': typeof StandaloneZendeskDataFields;
    'standalone/Zendesk/Settings/DataFields': typeof StandaloneZendeskDataFields;
  }
}
